import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Camera } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useToast } from '../../Admissionform/ToastManager';
import ProfileUploadModal from '../../../Login/ProfileUploadModal';
import OfferLetterActions from "./OfferLetterActions";
import './ProfileSidebar.css';
import { formatDistanceToNow } from "date-fns";


const ProfileSidebar = ({
    studentData,
    onStudentDataUpdate,
    showToast,
}) => {
    const [showUploadOptions, setShowUploadOptions] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);
    const { showSuccess, showError } = useToast();

    const videoRef = useRef(null);

    useEffect(() => {
        if (!studentData?.studentId) return;

        const fetchProfileAndResume = async () => {
            try {
                // 1️⃣ Fetch profile pic
                const profileRes = await axios.get(
                    `${process.env.REACT_APP_API_URL}/student/profile-pic/${studentData.studentId}`
                );

                let updatedData = { ...studentData };

                if (profileRes.data.success && profileRes.data.profilePicPath) {
                    updatedData.profilePicPath = profileRes.data.profilePicPath;
                }

                // 2️⃣ Fetch resume path
                const resumeRes = await axios.get(
                    `${process.env.REACT_APP_API_URL}/student/resume/${studentData.studentId}`
                );

                if (resumeRes.data.success && resumeRes.data.resumePath) {
                    updatedData.resumePath = resumeRes.data.resumePath;
                }

                // Update parent only if something changed
                onStudentDataUpdate?.(updatedData);

            } catch (error) {
                console.error("Error fetching profile/resume:", error);
            }
        };

        fetchProfileAndResume();
    }, [studentData?.studentId]);
    // ✅ no onStudentDataUpdate dependency


    const ImageCropper = ({ imageSrc, onCrop, onCancel }) => {
        const canvasRef = useRef(null);
        const imageRef = useRef(null);
        const containerRef = useRef(null);
        const [crop, setCrop] = useState({ x: 0, y: 0, width: 190, height: 190 });
        const [imageLoaded, setImageLoaded] = useState(false);
        const [imageObj, setImageObj] = useState(null);
        const [isDragging, setIsDragging] = useState(false);
        const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
        const [isResizing, setIsResizing] = useState(false);
        const [resizeHandle, setResizeHandle] = useState('');
        const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

        useEffect(() => {
            const img = new Image();
            img.onload = () => {
                setImageObj(img);
                setImageLoaded(true);

                // Calculate display size to fit in container (max 500px)
                const maxSize = 500;
                const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
                const displayWidth = img.width * scale;
                const displayHeight = img.height * scale;

                setDisplaySize({ width: displayWidth, height: displayHeight });

                // Set initial crop to center of image
                const cropSize = Math.min(displayWidth, displayHeight) * 0.6; // 60% of smaller dimension
                setCrop({
                    x: (displayWidth - cropSize) / 2,
                    y: (displayHeight - cropSize) / 2,
                    width: cropSize,
                    height: cropSize
                });
            };
            img.src = imageSrc;
        }, [imageSrc]);

        const handleMouseDown = (e, action, handle = '') => {
            e.preventDefault();
            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            if (action === 'drag') {
                setIsDragging(true);
                setDragStart({ x: x - crop.x, y: y - crop.y });
            } else if (action === 'resize') {
                setIsResizing(true);
                setResizeHandle(handle);
                setDragStart({ x, y });
            }
        };

        const handleMouseMove = useCallback((e) => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            if (isDragging) {
                const newX = Math.max(0, Math.min(x - dragStart.x, displaySize.width - crop.width));
                const newY = Math.max(0, Math.min(y - dragStart.y, displaySize.height - crop.height));

                setCrop(prev => ({
                    ...prev,
                    x: newX,
                    y: newY
                }));
            } else if (isResizing) {
                const deltaX = x - dragStart.x;
                const deltaY = y - dragStart.y;

                setCrop(prev => {
                    let newCrop = { ...prev };

                    switch (resizeHandle) {
                        case 'se': // bottom-right
                            newCrop.width = Math.max(50, Math.min(prev.width + deltaX, displaySize.width - prev.x));
                            newCrop.height = Math.max(50, Math.min(prev.height + deltaY, displaySize.height - prev.y));
                            break;
                        case 'sw': // bottom-left
                            const newWidth = Math.max(50, prev.width - deltaX);
                            const newX = Math.max(0, prev.x + prev.width - newWidth);
                            newCrop.x = newX;
                            newCrop.width = newWidth;
                            newCrop.height = Math.max(50, Math.min(prev.height + deltaY, displaySize.height - prev.y));
                            break;
                        case 'ne': // top-right
                            const newHeight = Math.max(50, prev.height - deltaY);
                            const newY = Math.max(0, prev.y + prev.height - newHeight);
                            newCrop.y = newY;
                            newCrop.height = newHeight;
                            newCrop.width = Math.max(50, Math.min(prev.width + deltaX, displaySize.width - prev.x));
                            break;
                        case 'nw': // top-left
                            const newWidthNW = Math.max(50, prev.width - deltaX);
                            const newHeightNW = Math.max(50, prev.height - deltaY);
                            const newXNW = Math.max(0, prev.x + prev.width - newWidthNW);
                            const newYNW = Math.max(0, prev.y + prev.height - newHeightNW);
                            newCrop.x = newXNW;
                            newCrop.y = newYNW;
                            newCrop.width = newWidthNW;
                            newCrop.height = newHeightNW;
                            break;
                        default:
                            // No action needed for unknown resize handle
                            break;
                    }

                    return newCrop;
                });

                setDragStart({ x, y });
            }
        }, [isDragging, isResizing, dragStart, crop, displaySize, resizeHandle]);

        const handleMouseUp = useCallback(() => {
            setIsDragging(false);
            setIsResizing(false);
            setResizeHandle('');
        }, []);

        useEffect(() => {
            if (isDragging || isResizing) {
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);

                return () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                };
            }
        }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

        const handleCrop = () => {
            if (!imageObj || !canvasRef.current) return;

            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            canvas.width = 190;
            canvas.height = 190;

            // Calculate actual coordinates on the original image
            const scaleX = imageObj.width / displaySize.width;
            const scaleY = imageObj.height / displaySize.height;

            const actualX = crop.x * scaleX;
            const actualY = crop.y * scaleY;
            const actualWidth = crop.width * scaleX;
            const actualHeight = crop.height * scaleY;

            // Draw the cropped image
            ctx.drawImage(
                imageObj,
                actualX, actualY, actualWidth, actualHeight,
                0, 0, 190, 190
            );

            canvas.toBlob((blob) => {
                onCrop(blob);
            }, 'image/jpeg', 0.9);
        };

        const resetCrop = () => {
            if (!imageLoaded) return;

            const cropSize = Math.min(displaySize.width, displaySize.height) * 0.6;
            setCrop({
                x: (displaySize.width - cropSize) / 2,
                y: (displaySize.height - cropSize) / 2,
                width: cropSize,
                height: cropSize
            });
        };

        return (
            <div className="image-cropper-modal">
                <div className="cropper-header">
                    <h3>Crop Your Image</h3>
                    <button className="close-btn" onClick={onCancel}>×</button>
                </div>
                <div className="cropper-content">
                    {imageLoaded && (
                        <>
                            <div className="crop-instructions">
                                <p>Drag the crop area to reposition, or drag the corners to resize.</p>
                            </div>
                            <div
                                ref={containerRef}
                                className="crop-container"
                                style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    userSelect: 'none'
                                }}
                            >
                                <img
                                    ref={imageRef}
                                    src={imageSrc}
                                    alt="Crop preview"
                                    className="crop-image"
                                    style={{
                                        width: `${displaySize.width}px`,
                                        height: `${displaySize.height}px`,
                                        display: 'block',
                                        border: '2px solid #ddd',
                                        borderRadius: '8px'
                                    }}
                                    draggable={false}
                                />

                                {/* Crop overlay */}
                                <div
                                    className="crop-overlay"
                                    style={{
                                        position: 'absolute',
                                        left: `${crop.x}px`,
                                        top: `${crop.y}px`,
                                        width: `${crop.width}px`,
                                        height: `${crop.height}px`,
                                        border: '2px solid #007bff',
                                        borderRadius: '4px',
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                                        boxSizing: 'border-box'
                                    }}
                                    onMouseDown={(e) => handleMouseDown(e, 'drag')}
                                >
                                    {/* Resize handles */}
                                    <div
                                        className="resize-handle resize-handle-nw"
                                        style={{
                                            position: 'absolute',
                                            top: '-6px',
                                            left: '-6px',
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: '#007bff',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            cursor: 'nw-resize'
                                        }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            handleMouseDown(e, 'resize', 'nw');
                                        }}
                                    />
                                    <div
                                        className="resize-handle resize-handle-ne"
                                        style={{
                                            position: 'absolute',
                                            top: '-6px',
                                            right: '-6px',
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: '#007bff',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            cursor: 'ne-resize'
                                        }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            handleMouseDown(e, 'resize', 'ne');
                                        }}
                                    />
                                    <div
                                        className="resize-handle resize-handle-sw"
                                        style={{
                                            position: 'absolute',
                                            bottom: '-6px',
                                            left: '-6px',
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: '#007bff',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            cursor: 'sw-resize'
                                        }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            handleMouseDown(e, 'resize', 'sw');
                                        }}
                                    />
                                    <div
                                        className="resize-handle resize-handle-se"
                                        style={{
                                            position: 'absolute',
                                            bottom: '-6px',
                                            right: '-6px',
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: '#007bff',
                                            border: '2px solid white',
                                            borderRadius: '50%',
                                            cursor: 'se-resize'
                                        }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            handleMouseDown(e, 'resize', 'se');
                                        }}
                                    />
                                </div>
                            </div>
                            <canvas
                                ref={canvasRef}
                                style={{ display: 'none' }}
                            />
                        </>
                    )}
                    <div className="cropper-controls">
                        <button className="reset-crop-btn" onClick={resetCrop}>
                            Reset
                        </button>
                        <button className="cancel-crop-btn" onClick={onCancel}>
                            Cancel
                        </button>
                        <button className="apply-crop-btn" onClick={handleCrop}>
                            Apply Crop
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const handleCancelCrop = () => {
        setShowImageCropper(false);
        setSelectedImageForCrop(null);
    };
    // Profile image URL with cache busting
    const getProfileImageUrl = (profilePicPath) => {
        if (!profilePicPath) return null;

        const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, ''); // remove trailing slash

        // If profilePicPath already starts with /uploads, do NOT add /api
        if (profilePicPath.startsWith('/uploads')) {
            return `${baseUrl.replace(/\/api$/, '')}${profilePicPath}?t=${Date.now()}`;
        }

        return `${baseUrl}${profilePicPath}?t=${Date.now()}`;
    };



    // Handle resume upload
    const handleResumeUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            showToast?.error('Only PDF files are allowed.');
            showError('Only PDF files are allowed.');
            event.target.value = '';
            return;
        }

        const formData = new FormData();
        formData.append('resume', file);
        formData.append('studentId', studentData.studentId);

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/upload-resume`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.success) {
                showToast?.success('Resume uploaded successfully!');
                showSuccess('Resume uploaded successfully!');

                // Update local data immediately so download button appears
                const updatedData = {
                    ...studentData,
                    resumePath: res.data.filePath,
                };
                onStudentDataUpdate?.(updatedData);
            } else {
                throw new Error(res.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Resume upload error:', error);
            showToast?.error(error.response?.data?.message || 'Failed to upload resume.');
            showError(error.response?.data?.message || 'Failed to upload resume.');
        } finally {
            event.target.value = '';
        }
    };

    // Handle profile picture upload
    const handleProfileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            setShowImageCropper(true);
            setSelectedImageForCrop(e.target.result);
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    // Handle cropped image upload
    const handleCroppedImageUpload = async (croppedBlob) => {
        const formData = new FormData();
        formData.append('profilePic', croppedBlob, 'cropped-profile.jpg');
        formData.append('studentId', studentData.studentId);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/upload-profile`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

            if (res.data.success) {
                const profilePicPath = res.data.filePath + '?t=' + Date.now();
                const updatedData = {
                    ...studentData,
                    profilePicPath: profilePicPath,
                };

                onStudentDataUpdate?.(updatedData);
                showToast?.success('Profile picture uploaded successfully!');
                showSuccess('Profile picture uploaded successfully!');
            } else {
                throw new Error(res.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Profile upload error:', error);
            showToast?.error(error.response?.data?.message || 'Failed to upload profile picture.');
            showError(error.response?.data?.message || 'Failed to upload profile picture.');
        } finally {
            setShowImageCropper(false);
            setSelectedImageForCrop(null);
        }
    };

    // Camera handling functions
    const handleCameraCapture = async () => {
        setShowUploadOptions(false);
        setShowCamera(true);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 }
            });
            setCameraStream(stream);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (error) {
            console.error('Error accessing camera:', error);
            alert('Could not access camera. Please check permissions.');
            setShowCamera(false);
        }
    };

    const capturePhoto = () => {
        const video = videoRef.current;
        if (!video) return;

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 190;
        canvas.height = 190;

        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const size = Math.min(videoWidth, videoHeight);
        const x = (videoWidth - size) / 2;
        const y = (videoHeight - size) / 2;

        context.drawImage(video, x, y, size, size, 0, 0, 190, 190);

        canvas.toBlob((blob) => {
            setCapturedImage({
                blob: blob,
                dataUrl: canvas.toDataURL('image/jpeg', 0.9)
            });
        }, 'image/jpeg', 0.9);
    };

    const retakePhoto = async () => {
        setCapturedImage(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { width: 640, height: 480 }
            });
            setCameraStream(stream);
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (error) {
            console.error('Error restarting camera:', error);
            alert('Could not restart camera. Please check permissions.');
            setShowCamera(false);
        }
    };

    const usePhoto = async () => {

        if (!capturedImage?.blob) {
            showToast?.error("No photo captured to upload.");
            showError("No photo captured to upload.");
            return;
        }

        const formData = new FormData();
        formData.append('profilePic', capturedImage.blob, 'captured-photo.jpg');
        formData.append('studentId', studentData.studentId);

        try {
            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/upload-profile`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

            if (res.data.success) {
                const profilePicPath = res.data.filePath + '?t=' + Date.now();
                const updatedData = {
                    ...studentData,
                    profilePicPath: profilePicPath,
                };

                onStudentDataUpdate?.(updatedData);
                showToast?.success('Photo captured and uploaded successfully!');
                showSuccess('Photo captured and uploaded successfully!');
            } else {
                throw new Error(res.data.message || 'Upload failed');
            }
        } catch (error) {
            console.error('Profile upload error:', error);
            showToast?.error(error.response?.data?.message || 'Failed to upload profile picture.');
            showError(error.response?.data?.message || 'Failed to upload profile picture.');
        } finally {
            closeCameraModal();
        }
    };

    const closeCameraModal = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setShowCamera(false);
        setCapturedImage(null);
    };



    if (!studentData) {
        return (
            <div className="profile-sidebar loading">
                <div>Loading profile...</div>
            </div>
        );
    }

    return (
        <div className="profiles-sidebar">
            <h2 className="student-id">{studentData.studentId}</h2>
            <span
                className="last-login-time"
                title={
                    studentData?.lastLogin
                        ? new Date(studentData.lastLogin).toLocaleString("en-IN", {
                            dateStyle: "medium",
                            timeStyle: "short",
                        })
                        : ""
                }
            >
                {studentData?.lastLogin && studentData.lastLogin !== "null"
                    ? formatDistanceToNow(new Date(studentData.lastLogin), { addSuffix: true })
                    : "Never logged in"}
            </span>

            {/* Profile Avatar */}
            <div className="profile-avatar">
                {studentData.profilePicPath ? (
                    <img
                        src={getProfileImageUrl(studentData.profilePicPath)}
                        alt="Profile"
                        className="profile-image"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                        }}
                    />
                ) : (
                    <FontAwesomeIcon icon={faUser} className="user12-icon" />
                )}
                {studentData.profilePicPath && (
                    <FontAwesomeIcon
                        icon={faUser}
                        className="user-icon"
                        style={{ display: 'none' }}
                    />
                )}
            </div>
            {showImageCropper && selectedImageForCrop && (
                <div className="modal-overlay">
                    <ImageCropper
                        imageSrc={selectedImageForCrop}
                        onCrop={handleCroppedImageUpload}
                        onCancel={handleCancelCrop}
                    />
                </div>
            )}

            {/* Upload Options Modal */}
            {showUploadOptions && (
                <ProfileUploadModal
                    isOpen={showUploadOptions}
                    onClose={() => setShowUploadOptions(false)}
                    onCameraCapture={handleCameraCapture}
                    onFileUpload={handleProfileUpload}
                    studentData={studentData}
                />
            )}

            {/* Camera Modal */}
            {showCamera && (
                <div className="modal-overlay">
                    <div className="camera-modal">
                        <div className="camera-header">
                            <h3>Take Profile Photo</h3>
                            <button className="close-btn" onClick={closeCameraModal}>×</button>
                        </div>

                        <div className="camera-content">
                            {!capturedImage ? (
                                <div className="camera-preview">
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        className="camera-video"
                                    />
                                    <div className="camera-controls">
                                        <button className="capture-btn" onClick={capturePhoto}>
                                            <Camera size={24} />
                                            Capture
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="photo-preview">
                                    <img
                                        src={capturedImage.dataUrl}
                                        alt="Captured"
                                        className="captured-photo"
                                    />
                                    <div className="photo-controls">
                                        <button className="retake-btn" onClick={retakePhoto}>
                                            Retake
                                        </button>
                                        <button className="use-photo-btn" onClick={usePhoto}>
                                            Use Photo
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden File Inputs */}
            <input
                id="profile-upload"
                type="file"
                accept="image/*"
                onChange={handleProfileUpload}
                style={{ display: "none" }}
            />

            <input
                id="resume-upload"
                type="file"
                accept="application/pdf"
                onChange={handleResumeUpload}
                style={{ display: "none" }}
            />

            {/* Sidebar Actions */}
            <div className="sidebar-actions">
                <div className="sidebar-group">
                    <button
                        className="upload-btn"
                        onClick={() => setShowUploadOptions(true)}
                    >
                        <Upload size={16} /> Upload Profile-Pic
                    </button>

                    <label className="sidebar-btn" htmlFor="resume-upload">
                        <Upload size={16} /> Upload Resume
                    </label>
                    {studentData.resumePath && (
                        <button
                            className="sidebar-btn"
                            onClick={async () => {
                                try {
                                    const response = await axios.get(
                                        `${process.env.REACT_APP_API_URL}/download/${studentData.studentId}`,
                                        { responseType: 'blob' } // important for file download
                                    );

                                    // Create a link element to trigger the download
                                    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = blobUrl;
                                    link.setAttribute('download', `${studentData.studentName}_${studentData.Department}_${studentData.branch}_Resume.pdf`);
                                    document.body.appendChild(link);
                                    link.click();
                                    link.remove();
                                    window.URL.revokeObjectURL(blobUrl);

                                    showToast?.success('Resume downloaded successfully!');
                                    showSuccess('Resume downloaded successfully!');
                                } catch (error) {
                                    console.error('Error downloading resume:', error);
                                    showToast?.error('Failed to download resume.');
                                    showError('Failed to download resume.');
                                }
                            }}
                        >
                            📄 Download Resume
                        </button>
                    )}
                    <OfferLetterActions
                        studentData={studentData}
                        onStudentDataUpdate={onStudentDataUpdate}
                    />
                </div>


            </div>
        </div>
    );
};

export default ProfileSidebar;